import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface ResourceCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentResourceCode?: string | null;
  employeeName?: string;
  onSave: (resourceCode: string) => Promise<boolean>;
}

export const ResourceCodeModal: React.FC<ResourceCodeModalProps> = ({
  open,
  onOpenChange,
  currentResourceCode,
  employeeName,
  onSave,
}) => {
  const [resourceCode, setResourceCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setResourceCode(currentResourceCode ?? '');
    }
  }, [open, currentResourceCode]);

  const handleSave = async () => {
    if (!resourceCode.trim()) return;

    setIsSubmitting(true);
    try {
      const success = await onSave(resourceCode.trim());
      if (success) {
        onOpenChange(false);
      }
    } catch (err) {
      console.error('Error saving resource code:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Resource Code</DialogTitle>
          <DialogDescription>
            Enter a resource code for{' '}
            {employeeName ? `${employeeName}` : 'this employee'} (max 4 characters)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="resource-code">Resource Code</Label>
            <Input
              id="resource-code"
              value={resourceCode}
              onChange={(e) => setResourceCode(e.target.value.slice(0, 4))}
              placeholder="Enter resource code"
              maxLength={4}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!resourceCode.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
